import { useEffect, type ReactNode } from "react";
import { Navigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isSupportedLocale, pickDefaultLocale } from "@/i18n/locale-utils";
import NotFound from "@/pages/NotFound";

/**
 * Validates the :locale URL parameter and syncs i18next + <html lang>.
 * - Supported locale → render children
 * - Looks like a locale (2 lowercase letters) but unsupported → 404 (so search
 *   engines don't follow phantom URLs and dilute hreflang signals)
 * - Anything else → redirect to user's preferred locale, preserving the rest
 */
export function LocaleGate({ children }: { children: ReactNode }) {
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();
  const { i18n } = useTranslation();

  const valid = isSupportedLocale(locale);
  const looksLikeLocale = !!locale && /^[a-z]{2}$/.test(locale);

  useEffect(() => {
    if (!valid) return;
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale!;
    }
  }, [locale, valid, i18n]);

  if (!valid) {
    // Invalid 2-letter locale (e.g. /zz/anything) → real 404, not a redirect.
    if (looksLikeLocale) return <NotFound />;
    // Otherwise, treat the whole path as a route under fallback locale.
    const fallback = pickDefaultLocale();
    const target = `/${fallback}${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}

